import { useState, useEffect, useCallback } from 'react'
import { supabase, supabaseRPC, nowGmtMinus3ISO } from '../lib/supabase'
import { StoredQuiz } from '../types'
import { generateShareId } from '../utils/helpers'

interface Project {
  id: string
  name: string
  user_id: string
  created_at: string
  updated_at: string
}

export interface ProjectMeta {
  id: string;
  name: string;
  quizzes: StoredQuiz[];
}

interface UseProjectsReturn {
  projects: ProjectMeta[]
  loading: boolean
  error: string | null
  createProject: (name: string) => Promise<ProjectMeta>
  updateProject: (id: string, data: Partial<ProjectMeta>) => Promise<ProjectMeta>
  deleteProject: (id: string) => Promise<void>
  loadProjects: () => Promise<void>
  clearProjects: () => void
  updateQuiz: (projectId: string, quizId: string, data: Partial<StoredQuiz>) => Promise<void>
}


export function useProjects(): UseProjectsReturn {
  const [projects, setProjects] = useState<ProjectMeta[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Buscar usuário atual
      const currentUserId = await supabaseRPC.getCurrentUserId()

      if (!currentUserId) {
        setProjects([])
        setLoading(false)
        return
      }

      // Buscar projetos do usuário atual
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', currentUserId)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Converter Project[] para ProjectMeta[] e buscar quizzes
      const projectsWithQuizzes = await Promise.all(
        (data || []).map(async (project: Project) => {
          // Buscar quizzes do projeto usando supabaseRPC
          const quizzes = await supabaseRPC.getQuizzesByProject(project.id)

          const projectMeta: ProjectMeta = {
            id: project.id,
            name: project.name,
            quizzes: (quizzes || []).map(q => ({
              id: q.id,
              name: q.name,
              slug: q.slug,
              data: q.data,
              status: q.status,
              share_id: q.share_id,
              createdAt: new Date(q.created_at).getTime(),
              updatedAt: new Date(q.updated_at).getTime()
            }))
          }

          return projectMeta
        })
      )

      setProjects(projectsWithQuizzes)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar projetos'
      setError(msg)
      console.error('Erro ao carregar projetos:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])


  const createProject = useCallback(async (name: string) => {
    try {
      const trimmed = name.trim()
      const normalized = trimmed.toLowerCase()
      if (!trimmed) {
        throw new Error('Nome do projeto inválido')
      }
      if (projects.some(p => (p.name || '').trim().toLowerCase() === normalized)) {
        throw new Error('Já existe um projeto com este nome')
      }
      // Buscar usuário atual
      const currentUserId = await supabaseRPC.getCurrentUserId()

      if (!currentUserId) {
        throw new Error('Usuário não autenticado')
      }

      // Criar projeto no banco
      const { data: newProject, error } = await supabase
        .from('projects')
        .insert([{
          name: trimmed || 'Novo Projeto',
          user_id: currentUserId,
          created_at: nowGmtMinus3ISO(),
          updated_at: nowGmtMinus3ISO()
        }])
        .select()
        .single()

      if (error) {
        console.error('Erro ao criar projeto:', error)
        throw error
      }

      if (newProject) {
        const projectMeta: ProjectMeta = {
          id: newProject.id,
          name: newProject.name,
          quizzes: []
        }
        setProjects(prev => [projectMeta, ...prev])
        console.log('Projeto criado no banco:', newProject)
        return projectMeta
      }

      throw new Error('Erro ao criar projeto')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao criar projeto'
      setError(errorMessage)
      console.error('Erro completo:', err)
      throw err
    }
  }, [projects])

  const updateProject = useCallback(async (id: string, data: Partial<ProjectMeta>) => {
    try {
      const { data: updatedProject, error } = await supabase
        .from('projects')
        .update({
          name: data.name,
          updated_at: nowGmtMinus3ISO()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      if (updatedProject) {
        setProjects(prev => prev.map(p =>
          p.id === id
            ? { ...p, name: updatedProject.name, quizzes: (data.quizzes ?? p.quizzes) }
            : p
        ))
      }

      return { id: updatedProject.id, name: updatedProject.name, quizzes: (data.quizzes ?? []) }
    } catch (err) {
      throw err instanceof Error ? err : new Error('Erro ao atualizar projeto')
    }
  }, [])

  const deleteProject = useCallback(async (id: string) => {
    try {
      const { error: quizzesError } = await supabase
        .from('quizzes')
        .delete()
        .eq('project_id', id)

      if (quizzesError) throw quizzesError

      const { error: projectError } = await supabase
        .from('projects')
        .delete()
        .eq('id', id)

      if (projectError) throw projectError

      setProjects(prev => prev.filter(p => p.id !== id))
    } catch (err) {
      throw err instanceof Error ? err : new Error('Erro ao deletar projeto')
    }
  }, [])

  const clearProjects = useCallback(() => {
    setProjects([])
    setLoading(false)
    setError(null)
  }, [])

  const updateQuiz = useCallback(async (projectId: string, quizId: string, data: Partial<StoredQuiz>) => {
    try {
      const { error } = await supabase
        .from('quizzes')
        .update({
          ...data,
          updated_at: nowGmtMinus3ISO()
        })
        .eq('id', quizId)

      if (error) throw error

      setProjects(prev => {
        const updated = prev.map(p =>
          p.id === projectId
            ? { ...p, quizzes: p.quizzes.map(q => q.id === quizId ? { ...q, ...data } : q) }
            : p
        );
        console.log('💾 [useProjects] updateQuiz: Projects atualizado:', {
          projectId,
          quizId,
          pagesCount: data.data?.pages?.length || 0,
          pagesOrder: data.data?.pages?.map((p: any, i: number) => ({ index: i, title: p.title })) || []
        });
        return updated;
      })
    } catch (err) {
      console.error('Erro ao atualizar quiz:', err)
      throw err instanceof Error ? err : new Error('Erro ao atualizar quiz')
    }
  }, [])

  return {
    projects,
    loading,
    error,
    createProject,
    updateProject,
    deleteProject,
    loadProjects,
    clearProjects,
    updateQuiz
  }
}